import React, { useState } from "react"

import { Resizable } from "re-resizable"

import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline"
import { Subscription } from "@rails/actioncable"

import dayjs from "src/dayjs"

import { gql, useClient, useQuery } from "hooks/urql"

import useClassPeriodRefresh from "../useClassPeriodRefresh"
import { RosterEntry } from "../useRoster"
import useStudentControlsManagement from "../useStudentControlsManagement"

import InviteStudentForm from "./InviteStudentForm"
import RosterList, { ExpectedStudent } from "./RosterList"

type ActiveClassPeriod = {
  startsAt: string
  endsAt: string
  acrClass: {
    name: string
    students: ExpectedStudent[]
  }
}

type RosterSidebarProps = {
  audioRoomId: string
  roster: Record<string, RosterEntry>
  localAttendeeId: string
  connected: boolean
  channel: Subscription
  showInviteForm?: boolean
  initialControlsUnlockedUserIds: string[]
}

const nameFor = (entry: Pick<RosterEntry, "fullName">) =>
  entry.fullName || "Participant"

const sortPresentUsers = (user: RosterEntry, otherUser: RosterEntry) => {
  const muteComparison =
    Number(user.muted !== false) - Number(otherUser.muted !== false)
  return muteComparison || nameFor(user).localeCompare(nameFor(otherUser))
}

const RosterSidebar: React.FC<RosterSidebarProps> = ({
  audioRoomId,
  roster,
  localAttendeeId,
  connected,
  channel,
  showInviteForm = false,
  initialControlsUnlockedUserIds,
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const {
    controlsUnlockedUserIds,
    hasPresentStudents,
    allControlsLocked,
    toggleAllControlsLocked,
    allStudentsMuted,
    muteAll,
  } = useStudentControlsManagement({
    initialControlsUnlockedUserIds,
    channel,
    roster,
    localAttendeeId,
  })
  const client = useClient()
  const [classPeriodsResult, refetchClassPeriods] = useQuery({
    query: classPeriodsQuery,
    client,
    variables: { audioRoomId },
  })
  const activeClassPeriod: ActiveClassPeriod | null | undefined =
    classPeriodsResult.data?.audioRoom?.activeAcrClassPeriod

  useClassPeriodRefresh(
    activeClassPeriod?.endsAt,
    classPeriodsResult.data?.audioRoom?.upcomingAcrClassPeriod?.startsAt,
    refetchClassPeriods
  )

  const controlsUnlockedUserIdSet = new Set(controlsUnlockedUserIds)
  const presentUsers = Object.values(roster)
    .filter(entry => entry.attendeeId !== localAttendeeId)
    .map(user => ({
      ...user,
      controlsLocked: user.userId
        ? !controlsUnlockedUserIdSet.has(user.userId)
        : false,
    }))
    .sort(sortPresentUsers)

  const presentUserIds = new Set(
    presentUsers.map(user => user.userId).filter(Boolean)
  )
  const absentExpectedStudents =
    activeClassPeriod?.acrClass.students
      .filter(student => !presentUserIds.has(student.id))
      .sort((student, otherStudent) =>
        nameFor(student).localeCompare(nameFor(otherStudent))
      ) ?? []

  const classPeriodHeading = activeClassPeriod
    ? `${activeClassPeriod.acrClass.name}, ${dayjs(
        activeClassPeriod.startsAt
      ).format("h:mm A")}-${dayjs(activeClassPeriod.endsAt).format("h:mm A")}`
    : "Audio Classroom"

  return collapsed ? (
    <aside className="flex w-12 items-start justify-center border-r-4 border-solid border-neutral-700 p-2">
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="rounded border-none bg-transparent p-1 text-white/60 hover:bg-white/10 hover:text-white"
        aria-label="Expand roster"
        title="Expand roster"
      >
        <ChevronDoubleRightIcon className="h-5 w-5" />
      </button>
    </aside>
  ) : (
    <Resizable
      defaultSize={{ width: 320, height: "100%" }}
      minWidth={224}
      maxWidth="40vw"
      enable={{ right: true }}
      handleClasses={{
        right: "z-10 w-2 cursor-col-resize hover:bg-white/20 focus:bg-white/20",
      }}
      className="min-h-0"
    >
      <aside className="relative h-full border-r-4 border-solid border-neutral-700">
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="absolute right-2 top-2 z-20 rounded border-none bg-transparent p-1 text-white/60 hover:bg-white/10 hover:text-white"
          aria-label="Collapse roster"
          title="Collapse roster"
        >
          <ChevronDoubleLeftIcon className="h-5 w-5" />
        </button>

        <div className="h-full overflow-y-auto p-6">
          <h1 className="mb-6 pr-4 text-xl font-semibold">
            {classPeriodHeading}
          </h1>

          {showInviteForm && <InviteStudentForm />}

          <div className="mb-6 space-y-2">
            <button
              type="button"
              onClick={muteAll}
              disabled={!connected || !hasPresentStudents || allStudentsMuted}
              className="w-full cursor-pointer rounded bg-red-600 px-4 py-2 text-lg font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              Mute all students
            </button>
            <button
              type="button"
              onClick={toggleAllControlsLocked}
              disabled={!connected || !hasPresentStudents}
              className="w-full cursor-pointer rounded bg-neutral-700 px-4 py-2 text-lg font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {allControlsLocked
                ? "Unlock student controls"
                : "Lock student controls"}
            </button>
          </div>

          <div className="space-y-6">
            <RosterList
              heading="Present"
              members={presentUsers}
              channel={channel}
            />
            {activeClassPeriod && (
              <RosterList
                heading="Not present"
                members={absentExpectedStudents}
              />
            )}
          </div>
        </div>
      </aside>
    </Resizable>
  )
}

const classPeriodsQuery = gql`
  query AudioRoomClassPeriods($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      activeAcrClassPeriod {
        startsAt
        endsAt
        acrClass {
          name
          students {
            id
            fullName
            defaultLiveTranslationLanguageCode
            defaultAudioPlaybackRate
          }
        }
      }
      upcomingAcrClassPeriod {
        startsAt
      }
    }
  }
`

export default RosterSidebar
